import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { Fragment, useMemo, useState } from "react";
import { useFinance } from "../FinanceContext";
import { metrics, sum } from "../data/model";
import type { BudgetItem } from "../data/types";
import { StatusBadge } from "./StatusChip";
import { toneColor } from "./tone";

/**
 * Shared grouped budget view — ported from the source prototype's `GroupBudget`,
 * used for both "Department Budget" (group by `dept`) and "Cost Centre Budget"
 * (group by `cc`).
 */
export function GroupBudgetTable({ by, label }: { by: "dept" | "cc"; label: string }) {
  const { model, fmt, scf, selMonth, openVouchers } = useFinance();
  const [open, setOpen] = useState<Set<string>>(new Set());

  const groups = useMemo(() => {
    const g: Record<string, BudgetItem[]> = {};
    model.items.forEach((it) => {
      const k = it[by];
      (g[k] ??= []).push(it);
    });
    return g;
  }, [model, by]);

  const totals = useMemo(() => {
    let annual = 0;
    let ytdB = 0;
    let ytdA = 0;
    model.items.forEach((it) => {
      const m = metrics(it, selMonth);
      annual += sum(it.planned) * scf;
      ytdB += m.ytdB * scf;
      ytdA += m.ytdA;
    });
    return { annual, ytdB, ytdA };
  }, [model, selMonth, scf]);

  function toggle(k: string) {
    const n = new Set(open);
    if (n.has(k)) n.delete(k);
    else n.add(k);
    setOpen(n);
  }

  const groupKeys = Object.keys(groups);

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
        <Button size="small" variant="outlined" onClick={() => setOpen(new Set(groupKeys))}>
          Expand all
        </Button>
        <Button size="small" variant="outlined" onClick={() => setOpen(new Set())}>
          Collapse all
        </Button>
      </Stack>
      <TableContainer sx={{ maxHeight: "70vh" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{label}</TableCell>
              <TableCell align="right">Annual Budget</TableCell>
              <TableCell align="right">YTD Budget</TableCell>
              <TableCell align="right">YTD Actual</TableCell>
              <TableCell align="right">Variance</TableCell>
              <TableCell align="right">Utilization</TableCell>
              <TableCell align="right">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupKeys.map((k) => {
              const its = groups[k] ?? [];
              let gAnn = 0;
              let gB = 0;
              let gA = 0;
              its.forEach((it) => {
                const m = metrics(it, selMonth);
                gAnn += sum(it.planned) * scf;
                gB += m.ytdB * scf;
                gA += m.ytdA;
              });
              const v = gB - gA;
              const u = gB ? gA / gB : 0;
              const st = !gB ? "g" : u > 1 ? "r" : u > 0.9 ? "a" : "g";
              const isOpen = open.has(k);
              return (
                <Fragment key={k}>
                  <TableRow
                    hover
                    onClick={() => toggle(k)}
                    sx={{ cursor: "pointer", bgcolor: "action.hover" }}
                  >
                    <TableCell sx={{ fontWeight: 700 }}>
                      {isOpen ? "▾" : "▸"} {k}
                    </TableCell>
                    <TableCell align="right">{fmt(gAnn)}</TableCell>
                    <TableCell align="right">{fmt(gB)}</TableCell>
                    <TableCell align="right">{fmt(gA)}</TableCell>
                    <TableCell align="right" sx={{ color: toneColor(v >= 0 ? "pos" : "neg") }}>
                      {fmt(v)}
                    </TableCell>
                    <TableCell align="right">{(u * 100).toFixed(1)}%</TableCell>
                    <TableCell align="right">
                      <StatusBadge status={st} />
                    </TableCell>
                  </TableRow>
                  {isOpen
                    ? its.map((it) => {
                        const m = metrics(it, selMonth);
                        return (
                          <TableRow key={it.id}>
                            <TableCell sx={{ pl: 4 }}>
                              {it.code} · {it.name}
                            </TableCell>
                            <TableCell align="right">{fmt(m.annual * scf)}</TableCell>
                            <TableCell align="right">{fmt(m.ytdB * scf)}</TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                color: "primary.main",
                                cursor: "pointer",
                                textDecoration: "underline dotted",
                              }}
                              onClick={() => openVouchers(it, "ytd")}
                            >
                              {fmt(m.ytdA)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: toneColor(m.ytdB * scf - m.ytdA >= 0 ? "pos" : "neg") }}
                            >
                              {fmt(m.ytdB * scf - m.ytdA)}
                            </TableCell>
                            <TableCell align="right">
                              {m.ytdB ? ((m.ytdA / (m.ytdB * scf)) * 100).toFixed(1) : 0}%
                            </TableCell>
                            <TableCell align="right">
                              <StatusBadge status={m.status} />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    : null}
                </Fragment>
              );
            })}
            <TableRow sx={{ "& td": { fontWeight: 800, borderTop: 2, borderColor: "divider" } }}>
              <TableCell>TOTAL</TableCell>
              <TableCell align="right">{fmt(totals.annual)}</TableCell>
              <TableCell align="right">{fmt(totals.ytdB)}</TableCell>
              <TableCell align="right">{fmt(totals.ytdA)}</TableCell>
              <TableCell
                align="right"
                sx={{ color: toneColor(totals.ytdB - totals.ytdA >= 0 ? "pos" : "neg") }}
              >
                {fmt(totals.ytdB - totals.ytdA)}
              </TableCell>
              <TableCell align="right">
                {totals.ytdB ? ((totals.ytdA / totals.ytdB) * 100).toFixed(1) : 0}%
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
        Grouped by {label.toLowerCase()} · click a row to expand cost heads.
      </Typography>
    </Box>
  );
}
